import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Text,
  Button,
  Stack,
  TextInput,
  Alert,
  Group,
  Loader,
} from '@mantine/core';
import { IconDeviceFloppy, IconAlertCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  savePDFProject,
  fileToBase64,
  projectNameExists,
  getProjectsStorageUsage,
  type PDFField,
} from '../../utils/storage/pdfProjects';
import { useTranslation } from '../../contexts/hooks';

interface SaveProjectModalProps {
  opened: boolean;
  onClose: () => void;
  pdfFile: File | null;
  fields: PDFField[];
  existingFields: PDFField[];
  currentPage: number;
  totalPages: number;
  defaultName?: string;
  currentProjectId?: string;
}

export function SaveProjectModal({
  opened,
  onClose,
  pdfFile,
  fields,
  existingFields,
  currentPage,
  totalPages,
  defaultName,
  currentProjectId,
}: SaveProjectModalProps) {
  const { t } = useTranslation();
  const [projectName, setProjectName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  const [storageUsage, setStorageUsage] = useState({ size: 0, count: 0 });
  const [nameExists, setNameExists] = useState(false);

  const generateUniqueName = useCallback(async () => {
    if (!pdfFile) return;
    
    const baseName = pdfFile.name.replace(/\.pdf$/i, '');
    let suggestedName = baseName;
    let counter = 1;
    
    // Find a unique name
    while (await projectNameExists(suggestedName, currentProjectId)) {
      suggestedName = `${baseName} (${counter})`;
      counter++;
    }
    
    setProjectName(suggestedName);
  }, [pdfFile, currentProjectId]);

  const checkNameExists = useCallback(async () => {
    try {
      const exists = await projectNameExists(projectName.trim(), currentProjectId);
      setNameExists(exists);
    } catch (error) {
      console.error('Failed to check if name exists:', error);
      setNameExists(false);
    }
  }, [projectName, currentProjectId]);

  const loadStorageUsage = useCallback(async () => {
    try {
      const usage = await getProjectsStorageUsage();
      setStorageUsage(usage);
    } catch (error) {
      console.error('Failed to load storage usage:', error);
      setStorageUsage({ size: 0, count: 0 });
    }
  }, []);

  useEffect(() => {
    if (opened) {
      // Set default name when modal opens
      if (defaultName) {
        setProjectName(defaultName);
      } else if (pdfFile) {
        generateUniqueName();
      }
      setNameError('');
      loadStorageUsage();
    }
  }, [opened, defaultName, pdfFile, currentProjectId, generateUniqueName, loadStorageUsage]);

  // Check if name exists when project name changes
  useEffect(() => {
    if (projectName.trim()) {
      checkNameExists();
    } else {
      setNameExists(false);
    }
  }, [projectName, currentProjectId, checkNameExists]);

  const validateProjectName = async (name: string): Promise<string> => {
    if (!name.trim()) {
      return t('projects.save.name.required');
    }
    if (name.length > 100) {
      return t('projects.save.name.toolong');
    }
    if (await projectNameExists(name.trim(), currentProjectId)) {
      return t('projects.save.name.exists.error');
    }
    return '';
  };

  const handleSave = async () => {
    if (!pdfFile) {
      notifications.show({
        title: t('projects.save.error'),
        message: t('projects.save.nopdf'),
        color: 'red',
      });
      return;
    }

    const trimmedName = projectName.trim();
    const error = await validateProjectName(trimmedName);
    if (error) {
      setNameError(error);
      return;
    }

    setIsLoading(true);
    try {
      // Convert PDF file to base64
      const pdfData = await fileToBase64(pdfFile);
      
      // Check storage size before saving
      const currentUsage = await getProjectsStorageUsage();
      const estimatedNewSize = pdfData.length + JSON.stringify(fields).length + JSON.stringify(existingFields).length;
      const maxStorage = 50 * 1024 * 1024; // 50MB limit for IndexedDB (much larger than localStorage)
      
      if (currentUsage.size + estimatedNewSize > maxStorage) {
        notifications.show({
          title: t('projects.save.storage.full'),
          message: t('projects.save.storage.full.detail'),
          color: 'orange',
        });
        return;
      }

      const success = await savePDFProject({
        name: trimmedName,
        pdfData,
        originalFileName: pdfFile.name,
        fields,
        existingFields,
        currentPage,
        totalPages,
      });

      if (success) {
        notifications.show({
          title: t('projects.save.success'),
          message: t('projects.save.success.detail.full').replace('{name}', trimmedName),
          color: 'green',
        });
        onClose();
      } else {
        throw new Error('Failed to save project');
      }
    } catch (error) {
      console.error('Error saving project:', error);
      notifications.show({
        title: t('projects.save.error'),
        message: t('projects.save.error.detail'),
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('projects.save.modal.title')}
      centered
    >
      <Stack gap="md">
        <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
          <Text size="sm">
            {t('projects.list.empty.detail')}
          </Text>
        </Alert>

        <TextInput
          label={t('projects.save.name.label')}
          placeholder={t('projects.save.name.placeholder')}
          value={projectName}
          onChange={(e) => {
            setProjectName(e.currentTarget.value);
            setNameError('');
          }}
          error={nameError}
          required
          maxLength={100}
        />

        {nameExists && (
          <Alert color="yellow" variant="light">
            <Text size="sm">
              {t('projects.save.name.exists')}
            </Text>
          </Alert>
        )}

        {pdfFile && (
          <Stack gap="xs">
            <Text size="sm" fw={500}>{t('projects.save.modal.title')} :</Text>
            <Text size="sm" c="dimmed">• {t('editor.field.page')} : {pdfFile.name}</Text>
            <Text size="sm" c="dimmed">• {t('projects.pages').replace('{count}', totalPages.toString()).replace('{plural}', totalPages !== 1 ? 's' : '')}</Text>
            <Text size="sm" c="dimmed">• {t('projects.fields').replace('{count}', (fields.length + existingFields.length).toString()).replace('{plural}', (fields.length + existingFields.length) !== 1 ? 's' : '')}</Text>
          </Stack>
        )}

        <Alert color="gray" variant="light">
          <Text size="xs">
            {t('projects.count')
              .replace('{count}', storageUsage.count.toString())
              .replace('{plural}', storageUsage.count !== 1 ? 's' : '')
              .replace('{size}', formatFileSize(storageUsage.size))}
          </Text>
        </Alert>

        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={onClose} disabled={isLoading}>
            {t('modal.cancel')}
          </Button>
          <Button
            leftSection={isLoading ? <Loader size={16} /> : <IconDeviceFloppy size={16} />}
            onClick={handleSave}
            disabled={isLoading || !projectName.trim()}
          >
            {isLoading ? t('projects.loading') : t('projects.save.button.confirm')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}